import CloseIcon from "@mui/icons-material/Close";
import { Alert, Box, Button, Grid, IconButton, Modal, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useMemo } from "react";
import * as yup from "yup";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { IVFormErros, useVForm, VForm, VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { unmaskCpfOrCnpj } from "../../../../shared/utils";

interface ModalConvertAnalysisToPjProps {
	isOpen: boolean;
	onClose: () => void;
	initialDocument?: string;
	initialName?: string;
	isLoading?: boolean;
	onConfirm: (data: { document: string; name: string }) => void | Promise<void>;
}

type FormData = {
	document: string;
	name: string;
};

const validationSchema: yup.SchemaOf<FormData> = yup.object().shape({
	document: yup
		.string()
		.required("Campo obrigatório")
		.test("is-cnpj", "Informe um CNPJ válido", (value) => {
			const raw = unmaskCpfOrCnpj(String(value ?? ""));
			return raw.length === 14;
		}),
	name: yup.string().required("Campo obrigatório"),
});

export const ModalConvertAnalysisToPj: React.FC<ModalConvertAnalysisToPjProps> = ({
	isOpen,
	onClose,
	initialDocument,
	initialName,
	isLoading = false,
	onConfirm,
}) => {
	const { formRef } = useVForm();
	const UnformForm = VForm as unknown as React.ComponentType<any>;

	const modalStyle = useMemo(
		() => ({
			position: "relative" as const,
			top: "50%",
			left: "50%",
			transform: "translate(-50%, -50%)",
			width: "min(720px, 92vw)",
			maxWidth: "70%",
			bgcolor: "background.paper",
			border: "1px solid #605e5e",
			boxShadow: 24,
			p: 2,
			borderRadius: "6px",
			display: "flex",
			flexDirection: "column" as const,
			backgroundColor: "#FFF",
		}),
		[],
	);

	useEffect(() => {
		if (!isOpen) return;
		formRef.current?.setErrors({});
		formRef.current?.setData({
			document: String(initialDocument ?? ""),
			name: String(initialName ?? ""),
		});
	}, [isOpen, initialDocument, initialName]);

	const handleSubmit = async (data: FormData) => {
		try {
			formRef.current?.setErrors({});
			const validated = await validationSchema.validate(data, { abortEarly: false });
			await onConfirm({
				document: unmaskCpfOrCnpj(validated.document),
				name: String(validated.name ?? "").trim(),
			});
		} catch (err: any) {
			if (err?.name === "ValidationError") {
				const validationErros: IVFormErros = {};
				err.inner.forEach((error: any) => {
					if (!error.path) return;
					validationErros[error.path] = error.message;
				});
				formRef.current?.setErrors(validationErros);
			}
		}
	};

	return (
		<Modal
			open={isOpen}
			aria-labelledby="modal-convert-analysis-to-pj-title"
			onClose={(_, reason) => {
				if (reason === "backdropClick") return;
				if (isLoading) return;
				onClose();
			}}
		>
			<Box sx={modalStyle}>
				<LoadingComp isopen={isLoading} />
				<Box position="relative" mb={0.5}>
					<Typography
						id="modal-convert-analysis-to-pj-title"
						variant="h5"
						color="primary"
						sx={{ fontWeight: 400, textAlign: "center", pr: 4 }}
					>
						Converter análise para PJ
					</Typography>
					<IconButton
						aria-label="Fechar"
						onClick={onClose}
						size="small"
						disabled={isLoading}
						sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<Alert
					severity="warning"
					variant="standard"
					sx={{
						border: "none",
						borderRadius: 2,
						backgroundColor: (theme) => alpha(theme.palette.warning.main, 0.08),
						"& .MuiAlert-icon": { color: "warning.main", mt: "2px" },
						"& .MuiAlert-message": { py: 0 },
					}}
				>
					<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.45 }}>
						Essa ação altera o tipo de pessoa do locatário para PJ.
						<br />
						Informe o CNPJ e a Razão Social/Nome para atualizar a análise.
					</Typography>
				</Alert>

				<Box mt={2}>
					<UnformForm ref={formRef} onSubmit={handleSubmit}>
						<Grid container spacing={2}>
							<Grid item xs={12} md={4}>
								<VMaskTextField name="document" label="CNPJ" mask="cnpj" disabled={isLoading} fullWidth />
							</Grid>
							<Grid item xs={12} md={8}>
								<VTextField name="name" label="Nome / Razão Social" disabled={isLoading} fullWidth />
							</Grid>
							<Grid item xs={12} display="flex" justifyContent="flex-end" gap={1}>
								<Button variant="outlined" onClick={onClose} disabled={isLoading}>
									Cancelar
								</Button>
								<Button
									variant="contained"
									disabled={isLoading}
									onClick={() => formRef.current?.submitForm()}
								>
									Confirmar conversão
								</Button>
							</Grid>
						</Grid>
					</UnformForm>
				</Box>
			</Box>
		</Modal>
	);
};
